<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable no-use-before-define */
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useEscClose } from 'dashboard/composables/useEscClose';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import ImovelAPI from 'dashboard/api/imoveis';
import LaisXmlFeedAPI from 'dashboard/api/laisXmlFeed';
import ListingImportsAPI from 'dashboard/api/listingImports';
import CaptacaoShelfSkeleton from '../components/CaptacaoShelfSkeleton.vue';
import {
  importRunTone,
  normalizeRun,
  normalizeRunsResponse,
  operationPayload,
  rawStatusEntries,
} from '../../imoveis/utils/listingImportOperations';
import {
  CAPTACAO_RUN_STATUS_OPTIONS,
  buildRunIntegrity,
  buildRunProvenanceItems,
  buildRunSummary,
  canReconcileRun,
  canRetryRun,
  runStatusLabel,
  sourceLabelForRun,
  withCaptacaoRunAuditFields,
} from '../utils/captacaoRunAudit';

const router = useRouter();
const route = useRoute();
const accountId = useMapGetter('getCurrentAccountId');
const { t } = useI18n();
const isImporting = ref(false);
const isLoadingXmlFeed = ref(false);
const isValidatingXml = ref(false);
const isDisablingXml = ref(false);
const xmlFeed = ref(null);
const xmlFeedHelp = ref({});
const lastImport = ref(null);
const importHistory = ref([]);
const importStatusError = ref(false);
const showCancelModal = ref(false);
const isCancelling = ref(false);
const showXmlHelp = ref(false);
const importRuns = ref([]);
const importRunsMeta = ref({
  count: 0,
  total_count: 0,
  page: 1,
  per_page: 10,
  total_pages: 0,
});
const selectedAuditRun = ref(null);
const runFilters = ref({
  status: '',
  origin: '',
});
const isLoadingImportRuns = ref(false);
const isLoadingRunDetail = ref(false);
const runAuditError = ref('');
const runOperationReason = ref('');
const activeRunAction = ref('');
const cancelReason = ref('');
const disableReason = ref('');

const OWNER_CAPTURE_CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'phone', label: 'Telefone' },
  { value: 'email', label: 'E-mail' },
  { value: 'in_person', label: 'Presencial' },
  { value: 'portal', label: 'Portal' },
  { value: 'other', label: 'Outro' },
];

const OWNER_CAPTURE_RESULTS = [
  { value: 'answered', label: 'Atendeu' },
  { value: 'callback_scheduled', label: 'Retorno agendado' },
  { value: 'no_answer', label: 'Não atendeu' },
  { value: 'message_sent', label: 'Mensagem enviada' },
  { value: 'email_sent', label: 'E-mail enviado' },
  { value: 'appraisal_scheduled', label: 'Avaliação agendada' },
  { value: 'not_interested', label: 'Sem interesse' },
  { value: 'do_not_contact', label: 'Não contatar' },
  { value: 'wrong_contact', label: 'Contato incorreto' },
  { value: 'already_listed', label: 'Já anunciado' },
  { value: 'mandate_signed', label: 'Mandato assinado' },
];

const OWNER_CAPTURE_NEXT_ACTION_RESULTS = new Set([
  'answered',
  'callback_scheduled',
  'no_answer',
  'message_sent',
  'email_sent',
  'appraisal_scheduled',
]);

const OWNER_CAPTURE_TERMINAL_STAGES = new Set(['mandated', 'lost']);

const OWNER_CAPTURE_STAGE_LABELS = {
  identified: 'Identificado',
  consent_pending: 'Consentimento pendente',
  contacted: 'Contatado',
  appraisal_scheduled: 'Avaliação agendada',
  appraisal_done: 'Avaliação concluída',
  negotiating: 'Negociando',
  mandated: 'Mandato assinado',
  lost: 'Perdido',
};

const OWNER_CAPTURE_RISK_LABELS = {
  owner_do_not_contact: 'DNC ativo',
  owner_phone_blacklisted: 'Telefone bloqueado',
  weekly_reveal_limit_reached: 'Limite semanal atingido',
  weekly_reveal_high: 'Reveal alto na semana',
  weekly_attempt_frequency_high: 'Frequência alta',
  next_action_overdue: 'Próxima ação vencida',
  pii_redacted_no_reveal_authorization: 'PII redigida',
};

const OWNER_CAPTURE_RISK_CLASSES = {
  critical: 'bg-rose-50 text-rose-700 border-rose-100',
  high: 'bg-[#fff7ed] text-[#c2410c] border-orange-100',
  medium: 'bg-amber-50 text-amber-700 border-amber-100',
  info: 'bg-sky-50 text-sky-700 border-sky-100',
};

const PORTAIS = [
  {
    value: 'imovelweb',
    label: 'ImovelWeb',
    icon: 'i-lucide-home',
    color: '#3b82f6',
  },
  {
    value: 'plug_imoveis',
    label: 'Plug Imóveis',
    icon: 'i-lucide-plug',
    color: '#e9b76f',
  },
  {
    value: 'chaves_na_mao',
    label: 'Chaves na Mão',
    icon: 'i-lucide-key',
    color: '#28E4A8',
  },
  {
    value: 'df_imoveis',
    label: 'DF Imóveis',
    icon: 'i-lucide-home',
    color: '#FF8585',
  },
  {
    value: 'casa_mineira',
    label: 'Casa Mineira',
    icon: 'i-lucide-home',
    color: '#944f7a',
  },
  {
    value: 'casa_jau',
    label: 'Casa Jau',
    icon: 'i-lucide-home',
    color: '#b45309',
  },
  {
    value: 'abc_imovel',
    label: 'ABC Imóvel',
    icon: 'i-lucide-home',
    color: '#4a5568',
  },
  {
    value: 'lead_4_sales',
    label: 'Lead 4 Sales',
    icon: 'i-lucide-trending-up',
    color: '#0ea5e9',
  },
  {
    value: 'imoveis_sc',
    label: 'Imóveis-SC',
    icon: 'i-lucide-home',
    color: '#059669',
  },
  {
    value: 'sp_imovel',
    label: 'SP Imóvel',
    icon: 'i-lucide-home',
    color: '#7c3aed',
  },
  {
    value: 'litoral_sp',
    label: 'Litoral SP Imóvel',
    icon: 'i-lucide-home',
    color: '#06b6d4',
  },
  {
    value: 'guia_de_imoveis',
    label: 'Guia de Imóveis',
    icon: 'i-lucide-map',
    color: '#1FB386',
  },
  {
    value: 'guia_de_imoveis_abc',
    label: 'Guia de Imóveis ABC',
    icon: 'i-lucide-map',
    color: '#1FB386',
  },
  {
    value: 'guia_de_imoveis_at',
    label: 'Guia de Imóveis AT',
    icon: 'i-lucide-map',
    color: '#1FB386',
  },
  {
    value: 'guia_de_imoveis_atibaia',
    label: 'Guia de Imóveis Atibaia',
    icon: 'i-lucide-map',
    color: '#1FB386',
  },
  {
    value: 'guia_de_imoveis_braganca',
    label: 'Guia de Imóveis Bragança',
    icon: 'i-lucide-map',
    color: '#1FB386',
  },
  {
    value: 'guia_de_imoveis_campinas',
    label: 'Guia de Imóveis Campinas',
    icon: 'i-lucide-map',
    color: '#1FB386',
  },
  {
    value: 'guia_de_imoveis_guarulhos',
    label: 'Guia de Imóveis Guarulhos',
    icon: 'i-lucide-map',
    color: '#1FB386',
  },
  {
    value: 'guia_de_imoveis_litoral',
    label: 'Guia de Imóveis Litoral',
    icon: 'i-lucide-map',
    color: '#1FB386',
  },
  {
    value: 'guia_de_imoveis_osasco',
    label: 'Guia de Imóveis Osasco',
    icon: 'i-lucide-map',
    color: '#1FB386',
  },
  {
    value: 'guia_de_imoveis_sp',
    label: 'Guia de Imóveis SP',
    icon: 'i-lucide-map',
    color: '#1FB386',
  },
  {
    value: 'guia_de_imoveis_sorocaba',
    label: 'Guia de Imóveis Sorocaba',
    icon: 'i-lucide-map',
    color: '#1FB386',
  },
  {
    value: 'zn_imovel',
    label: 'ZN Imóvel',
    icon: 'i-lucide-home',
    color: '#2563eb',
  },
  {
    value: 'zl_imovel',
    label: 'ZL Imóvel',
    icon: 'i-lucide-home',
    color: '#2563eb',
  },
  {
    value: 'zo_imovel',
    label: 'ZO Imóvel',
    icon: 'i-lucide-home',
    color: '#2563eb',
  },
  {
    value: 'zs_imovel',
    label: 'ZS Imóvel',
    icon: 'i-lucide-home',
    color: '#2563eb',
  },
  {
    value: 'site_proprio',
    label: 'Site Próprio (XML)',
    icon: 'i-lucide-globe',
    color: '#49317a',
  },
  {
    value: 'outro',
    label: 'Outro portal (VRSync XML)',
    icon: 'i-lucide-link',
    color: '#a0aec0',
  },
];

const form = ref({
  portal: 'site_proprio',
  url: '',
  refresh_interval_hours: 24,
});

const ownerLookup = ref({
  imovel_id: '',
  owner_relationship_id: '',
  contact_id: '',
  capture_opportunity_id: '',
});
const ownerPortfolio = ref(null);
const ownerPortfolioError = ref('');
const isLoadingOwnerPortfolio = ref(false);
const isRecordingOwnerAttempt = ref(false);
const ownerAttemptForm = ref({
  channel: 'whatsapp',
  result: 'callback_scheduled',
  next_action_at: '',
  responsible_user_id: '',
  reason: '',
});

const IMPORT_STATUS = {
  enqueued: {
    label: 'Em processamento',
    cls: 'bg-sky-50 text-sky-700',
  },
  processed: {
    label: 'Concluída',
    cls: 'bg-emerald-50 text-emerald-700',
  },
  failed: {
    label: 'Falhou',
    cls: 'bg-rose-50 text-rose-700',
  },
  cancelado: {
    label: 'Cancelada',
    cls: 'bg-slate-100 text-slate-700',
  },
  canceled: {
    label: 'Cancelada',
    cls: 'bg-slate-100 text-slate-700',
  },
};

const importStatusMeta = status =>
  IMPORT_STATUS[status] || {
    label: status || 'Em andamento',
    cls: 'bg-slate-100 text-slate-700',
  };

const XML_FEED_STATUS = {
  not_configured: {
    label: 'Não configurado',
    cls: 'bg-slate-100 text-slate-700',
  },
  pending_validation: {
    label: 'Validando',
    cls: 'bg-amber-50 text-amber-700',
  },
  valid: {
    label: 'Pronto',
    cls: 'bg-emerald-50 text-emerald-700',
  },
  stale: {
    label: 'Desatualizado',
    cls: 'bg-[#fff7ed] text-[#c2410c]',
  },
  failing: {
    label: 'Falhando',
    cls: 'bg-rose-50 text-rose-700',
  },
  disabled: {
    label: 'Desativado',
    cls: 'bg-slate-100 text-slate-700',
  },
};

const xmlFeedStatusMeta = status =>
  XML_FEED_STATUS[status] || XML_FEED_STATUS.not_configured;

const selectedPortal = computed(
  () => PORTAIS.find(portal => portal.value === form.value.portal) || PORTAIS[0]
);
const runStatusOptions = CAPTACAO_RUN_STATUS_OPTIONS;
const importRunSummary = computed(() =>
  buildRunSummary(importRuns.value, importRunsMeta.value)
);
const hasImportRuns = computed(() => importRuns.value.length > 0);
const hasPreviousRunPage = computed(
  () => Number(importRunsMeta.value.page || 1) > 1
);
const hasNextRunPage = computed(
  () =>
    Number(importRunsMeta.value.page || 1) <
    Number(importRunsMeta.value.total_pages || 0)
);
const selectedRunIntegrity = computed(() =>
  buildRunIntegrity(selectedAuditRun.value)
);
const selectedRunProvenanceItems = computed(() =>
  buildRunProvenanceItems(selectedAuditRun.value)
);
const hasSelectedRunErrors = computed(
  () => selectedRunIntegrity.value.errors.length > 0
);
const canRetrySelectedRun = computed(() => canRetryRun(selectedAuditRun.value));
const canReconcileSelectedRun = computed(() =>
  canReconcileRun(selectedAuditRun.value)
);
const importOperationsRoute = computed(() => ({
  name: 'imoveis_import_operations',
  params: { accountId: accountId.value },
}));

const ownerPortfolioPayload = computed(() => ownerPortfolio.value || {});
const ownerPayload = computed(() => ownerPortfolioPayload.value.owner || {});
const ownerCapture = computed(() => ownerPortfolioPayload.value.capture || {});
const ownerAttempts = computed(
  () => ownerPortfolioPayload.value.attempts || {}
);
const ownerCompliance = computed(
  () => ownerPortfolioPayload.value.compliance || {}
);
const ownerFrequency = computed(
  () => ownerPortfolioPayload.value.frequency || {}
);
const ownerRiskFlags = computed(
  () => ownerPortfolioPayload.value.risk_flags || []
);
const ownerPortfolioProperties = computed(
  () => ownerPortfolioPayload.value.portfolio?.properties || []
);
const ownerCaptureStage = computed(() => ownerCapture.value.stage || '');
const ownerCaptureOpportunityId = computed(
  () => ownerCapture.value.capture_opportunity_id || ''
);
const ownerRevealLogId = computed(
  () =>
    ownerPayload.value.reveal_authorization?.reveal_log_id ||
    ownerCapture.value.source_owner_contact_id ||
    ''
);
const ownerAttemptRequiresNextAction = computed(() =>
  OWNER_CAPTURE_NEXT_ACTION_RESULTS.has(ownerAttemptForm.value.result)
);
const ownerCaptureTerminal = computed(() =>
  OWNER_CAPTURE_TERMINAL_STAGES.has(ownerCaptureStage.value)
);
const ownerContactBlocked = computed(
  () =>
    ownerCompliance.value.do_not_contact || ownerCompliance.value.blacklisted
);
const ownerCanRecordAttempt = computed(
  () =>
    Boolean(ownerCaptureOpportunityId.value) &&
    !ownerCaptureTerminal.value &&
    !ownerContactBlocked.value
);
const ownerAttemptBlockedReason = computed(() => {
  if (!ownerCaptureOpportunityId.value) return 'Nenhuma oportunidade ativa';
  if (ownerCaptureTerminal.value) return 'Oportunidade encerrada';
  if (ownerContactBlocked.value) return 'Contato bloqueado';
  return '';
});

const xmlSupportMailto = computed(() => {
  const portalLabel = selectedPortal.value?.label || 'portal/CRM';
  const subject = encodeURIComponent(
    `Solicitar URL do XML de imóveis - ${portalLabel}`
  );
  const body = encodeURIComponent(
    [
      `Olá, preciso da URL pública do feed XML/VRSync de imóveis da minha conta no ${portalLabel}.`,
      'A URL precisa abrir no navegador sem login e listar os anúncios ativos para integração com a Lais.',
      'Podem me informar onde encontro esse link ou liberar o feed XML para minha conta?',
    ].join('\n\n')
  );
  return `mailto:?subject=${subject}&body=${body}`;
});

const formatDateTime = value =>
  value ? new Date(value).toLocaleString('pt-BR') : 'Ainda não registrado';

const formatShortDateTime = value =>
  value ? new Date(value).toLocaleString('pt-BR') : '—';

const isPresent = value =>
  value !== undefined && value !== null && value !== '';

const compactObject = object =>
  Object.entries(object).reduce((acc, [key, value]) => {
    if (isPresent(value)) acc[key] = value;
    return acc;
  }, {});

const normalizeCaptacaoRun = rawRun =>
  withCaptacaoRunAuditFields(normalizeRun(rawRun), rawRun);

const normalizeCaptacaoRunsResponse = response => {
  const normalized = normalizeRunsResponse(response);
  const rawRuns = Array.isArray(response?.payload) ? response.payload : [];

  return {
    runs: rawRuns.map(normalizeCaptacaoRun),
    meta: normalized.meta,
  };
};

const runToneClass = status => {
  const tone = importRunTone(status);
  if (tone === 'positive') return 'bg-emerald-50 text-emerald-700';
  if (tone === 'danger') return 'bg-rose-50 text-rose-700';
  if (tone === 'warning') return 'bg-amber-50 text-amber-700';
  if (tone === 'info') return 'bg-sky-50 text-sky-700';
  return 'bg-slate-100 text-slate-700';
};

const runActionReason = () =>
  runOperationReason.value.trim() || 'Ajuste operacional via Captação';

const runActionPayload = (action, id) =>
  operationPayload({
    action,
    id,
    reason: runActionReason(),
  });

const runQueryParams = () =>
  compactObject({
    page: importRunsMeta.value.page || 1,
    per_page: importRunsMeta.value.per_page || 10,
    status: runFilters.value.status,
    portal: runFilters.value.origin.trim(),
  });

const imovelRoute = imovelId => ({
  name: 'imovel_show',
  params: { accountId: accountId.value, id: imovelId },
});

const carregarRunsCaptacao = async () => {
  isLoadingImportRuns.value = true;
  runAuditError.value = '';
  try {
    const { data } = await ListingImportsAPI.runs(runQueryParams());
    const normalized = normalizeCaptacaoRunsResponse(data);
    importRuns.value = normalized.runs;
    importRunsMeta.value = {
      ...importRunsMeta.value,
      ...normalized.meta,
    };
    if (!selectedAuditRun.value && importRuns.value[0]) {
      selectedAuditRun.value = importRuns.value[0];
    }
  } catch (error) {
    runAuditError.value =
      error.response?.data?.error ||
      'Não foi possível carregar os runs de captação.';
    useAlert(runAuditError.value);
  } finally {
    isLoadingImportRuns.value = false;
  }
};

const aplicarFiltrosRuns = () => {
  importRunsMeta.value = { ...importRunsMeta.value, page: 1 };
  selectedAuditRun.value = null;
  carregarRunsCaptacao();
};

const mudarPaginaRuns = direction => {
  const nextPage = Number(importRunsMeta.value.page || 1) + direction;
  if (nextPage < 1) return;
  if (
    importRunsMeta.value.total_pages &&
    nextPage > Number(importRunsMeta.value.total_pages)
  ) {
    return;
  }

  importRunsMeta.value = { ...importRunsMeta.value, page: nextPage };
  carregarRunsCaptacao();
};

const carregarDetalheRun = async run => {
  if (!run?.id) return;

  selectedAuditRun.value = run;
  isLoadingRunDetail.value = true;
  try {
    const { data } = await ListingImportsAPI.showRun(run.id);
    selectedAuditRun.value = normalizeCaptacaoRun(data?.payload || run);
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível carregar os detalhes deste run.'
    );
  } finally {
    isLoadingRunDetail.value = false;
  }
};

const executarAcaoRun = async ({ run, action, request, successMessage }) => {
  if (!run?.id) return;

  activeRunAction.value = `${run.id}:${action}`;
  try {
    const { data } = await request();
    if (data?.payload)
      selectedAuditRun.value = normalizeCaptacaoRun(data.payload);
    useAlert(successMessage);
    await carregarRunsCaptacao();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível concluir a ação.'
    );
  } finally {
    activeRunAction.value = '';
  }
};

const retryRunCaptacao = run => {
  if (!canRetryRun(run)) return;

  executarAcaoRun({
    run,
    action: 'retry',
    request: () =>
      ListingImportsAPI.retryRun(run.id, runActionPayload('retry', run.id)),
    successMessage: 'Retry/reprocessamento solicitado.',
  });
};

const reconciliarRunCaptacao = run => {
  if (!canReconcileRun(run)) return;

  executarAcaoRun({
    run,
    action: 'reconcile',
    request: () =>
      ListingImportsAPI.releaseRunQuarantine(
        run.id,
        runActionPayload('release-run-quarantine', run.id)
      ),
    successMessage: 'Reconciliação/liberação solicitada.',
  });
};

const isRunActionLoading = (run, action) =>
  activeRunAction.value === `${run?.id}:${action}`;

const toDateTimeLocalValue = value => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const pad = number => String(number).padStart(2, '0');
  return [
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  ].join('T');
};

const fromDateTimeLocalValue = value => {
  if (!value) return '';

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const ownerStageLabel = stage =>
  OWNER_CAPTURE_STAGE_LABELS[stage] || stage || 'Sem estágio';

const ownerRiskLabel = code => OWNER_CAPTURE_RISK_LABELS[code] || code;

const ownerRiskClass = severity =>
  OWNER_CAPTURE_RISK_CLASSES[severity] ||
  'bg-slate-100 text-slate-700 border-slate-200';

const ownerDisplayValue = value => {
  if (!value) return '—';
  if (String(value).includes('[REDACTED]')) return 'Redigido';
  return value;
};

const ownerPortfolioErrorMessage = error => {
  if (error.response?.status === 403) {
    return 'Sem permissão para acessar a carteira deste proprietário.';
  }
  if (error.response?.status === 404) {
    return 'Carteira de proprietário não encontrada para este imóvel.';
  }
  return (
    error.response?.data?.error ||
    error.response?.data?.errors?.[0] ||
    'Não foi possível carregar a carteira do proprietário.'
  );
};

const hydrateOwnerAttemptForm = () => {
  ownerAttemptForm.value = {
    ...ownerAttemptForm.value,
    next_action_at: toDateTimeLocalValue(ownerAttempts.value.next?.at),
    responsible_user_id:
      ownerAttempts.value.next?.responsible_user_id ||
      ownerCapture.value.responsible_user_id ||
      '',
  };
};

let pollTimer = null;

function agendarPoll() {
  if (pollTimer) clearTimeout(pollTimer);
  if (lastImport.value && lastImport.value.status === 'enqueued') {
    pollTimer = setTimeout(carregarUltimoImport, 5000);
  }
}

const carregarUltimoImport = async () => {
  try {
    const { data } = await ImovelAPI.importStatus();
    importStatusError.value = false;
    lastImport.value = data.last_import || null;
    importHistory.value = data.history || [];
    agendarPoll();
  } catch (error) {
    importStatusError.value = true;
    useAlert(
      t('CAPTACAO.ALERTS.IMPORT_STATUS_ERROR', { message: error.message })
    );
  }
};

const applyXmlFeedPayload = payload => {
  xmlFeed.value = payload.data || null;
  xmlFeedHelp.value = payload.help || {};
  importHistory.value = payload.history || importHistory.value;
  if (xmlFeed.value) {
    form.value.portal = xmlFeed.value.portal || form.value.portal;
    form.value.url = xmlFeed.value.source_url || form.value.url;
    form.value.refresh_interval_hours =
      xmlFeed.value.refresh_interval_hours || 24;
  }
};

const carregarXmlFeed = async () => {
  isLoadingXmlFeed.value = true;
  try {
    const { data } = await LaisXmlFeedAPI.get();
    applyXmlFeedPayload(data);
  } catch (error) {
    useAlert(t('CAPTACAO.ALERTS.SHELF_HEALTH_ERROR'));
  } finally {
    isLoadingXmlFeed.value = false;
  }
};

const ownerPortfolioQueryParams = () =>
  compactObject({
    owner_relationship_id: ownerLookup.value.owner_relationship_id,
    contact_id: ownerLookup.value.contact_id,
    capture_opportunity_id: ownerLookup.value.capture_opportunity_id,
  });

const carregarCarteiraProprietario = async () => {
  const imovelId = String(ownerLookup.value.imovel_id || '').trim();
  if (!imovelId) {
    useAlert('Informe o ID do imóvel.');
    return;
  }

  isLoadingOwnerPortfolio.value = true;
  ownerPortfolioError.value = '';
  try {
    const { data } = await ImovelAPI.ownerContactPortfolio(
      imovelId,
      ownerPortfolioQueryParams()
    );
    ownerPortfolio.value = data?.payload || data || null;
    hydrateOwnerAttemptForm();
  } catch (error) {
    ownerPortfolio.value = null;
    ownerPortfolioError.value = ownerPortfolioErrorMessage(error);
    useAlert(ownerPortfolioError.value);
  } finally {
    isLoadingOwnerPortfolio.value = false;
  }
};

const ownerAttemptPayload = () =>
  compactObject({
    capture_opportunity_id: ownerCaptureOpportunityId.value,
    log_id: ownerRevealLogId.value,
    channel: ownerAttemptForm.value.channel,
    result: ownerAttemptForm.value.result,
    next_action_at: ownerAttemptRequiresNextAction.value
      ? fromDateTimeLocalValue(ownerAttemptForm.value.next_action_at)
      : '',
    responsible_user_id: ownerAttemptForm.value.responsible_user_id,
    reason: ownerAttemptForm.value.reason,
    metadata: {
      source: 'lais_captacao_owner_capture_ui',
      submitted_at: new Date().toISOString(),
    },
  });

const registrarTentativaProprietario = async () => {
  if (!ownerCanRecordAttempt.value) {
    useAlert(ownerAttemptBlockedReason.value || 'Tentativa indisponível.');
    return;
  }

  if (
    ownerAttemptRequiresNextAction.value &&
    !ownerAttemptForm.value.next_action_at
  ) {
    useAlert('Informe a próxima ação.');
    return;
  }

  isRecordingOwnerAttempt.value = true;
  try {
    const imovelId = String(ownerLookup.value.imovel_id || '').trim();
    await ImovelAPI.recordOwnerCaptureAttempt(imovelId, ownerAttemptPayload(), {
      idempotencyKey: [
        'p34-owner-capture-ui',
        ownerCaptureOpportunityId.value,
        Date.now(),
      ].join(':'),
    });
    useAlert('Tentativa registrada.');
    ownerLookup.value.capture_opportunity_id = ownerCaptureOpportunityId.value;
    await carregarCarteiraProprietario();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.response?.data?.errors?.[0] ||
        'Não foi possível registrar a tentativa.'
    );
  } finally {
    isRecordingOwnerAttempt.value = false;
  }
};

onBeforeUnmount(() => {
  if (pollTimer) clearTimeout(pollTimer);
});

useEscClose(showCancelModal, () => {
  showCancelModal.value = false;
});

const solicitarCancelamento = () => {
  cancelReason.value = '';
  showCancelModal.value = true;
};

const definirAreaAtuacao = () => {
  router.push({
    name: 'minha_lais_index',
    params: { accountId: accountId.value },
    query: { tab: 'origens' },
  });
};

const voltar = () => {
  if (typeof window !== 'undefined' && window.history.length > 1) {
    router.back();
    return;
  }

  router.push({
    name: 'imoveis_index',
    params: { accountId: accountId.value },
  });
};

const cancelarImport = async () => {
  const reason = cancelReason.value.trim();
  if (!reason) {
    useAlert('Informe o motivo do cancelamento.');
    return;
  }

  isCancelling.value = true;
  try {
    const audit = {
      source: 'lais_captacao_ui',
      requested_at: new Date().toISOString(),
      token: lastImport.value?.token,
    };
    lastImport.value = {
      ...(lastImport.value || {}),
      status: 'cancelado',
      cancel_reason: reason,
      cancel_audit: audit,
    };
    await ImovelAPI.cancelImport?.({
      token: lastImport.value?.token,
      reason,
      source: audit.source,
      audit,
    });
    useAlert('Importação cancelada');
    showCancelModal.value = false;
    cancelReason.value = '';
    await carregarUltimoImport();
  } catch (e) {
    useAlert(t('CAPTACAO.ALERTS.CANCEL_ERROR'));
  } finally {
    isCancelling.value = false;
  }
};

const importar = async () => {
  if (!form.value.url) {
    useAlert('Informe a URL do XML.');
    return;
  }
  isImporting.value = true;
  try {
    const { data } = await LaisXmlFeedAPI.import({
      url: form.value.url,
      portal: form.value.portal,
      refresh_interval_hours: form.value.refresh_interval_hours,
    });
    useAlert(`Importação enfileirada (${data.data?.portal || 'XML'}).`);
    applyXmlFeedPayload(data);
    await carregarUltimoImport();
  } catch (e) {
    useAlert(
      e.response?.data?.errors?.[0] || t('CAPTACAO.ALERTS.IMPORT_START_ERROR')
    );
  } finally {
    isImporting.value = false;
  }
};

const validarXml = async () => {
  if (!form.value.url) {
    useAlert('Informe a URL do XML.');
    return;
  }

  isValidatingXml.value = true;
  try {
    const { data } = await LaisXmlFeedAPI.validate({
      url: form.value.url,
      portal: form.value.portal,
      refresh_interval_hours: form.value.refresh_interval_hours,
    });
    applyXmlFeedPayload(data);
    useAlert(
      data.validation?.status === 'valid'
        ? 'XML validado com sucesso.'
        : 'XML validado com pendências.'
    );
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] ||
        t('CAPTACAO.ALERTS.XML_VALIDATE_ERROR')
    );
    await carregarXmlFeed();
  } finally {
    isValidatingXml.value = false;
  }
};

const desativarXmlFeed = async () => {
  const reason = disableReason.value.trim();
  if (!reason) {
    useAlert('Informe o motivo da desativação.');
    return;
  }

  isDisablingXml.value = true;
  try {
    const { data } = await LaisXmlFeedAPI.disable({
      reason,
      source: 'lais_captacao_ui',
      audit: {
        requested_at: new Date().toISOString(),
        feed_id: xmlFeed.value?.id,
      },
    });
    applyXmlFeedPayload(data);
    useAlert('Prateleira XML desativada.');
    disableReason.value = '';
  } catch (error) {
    useAlert(t('CAPTACAO.ALERTS.XML_DISABLE_ERROR'));
  } finally {
    isDisablingXml.value = false;
  }
};

const input =
  'w-full px-3 py-2.5 text-[14px] text-[#1a1a1a] bg-white border border-[#e5e7eb] rounded-lais-sm focus:outline-none focus:border-[#49317a] focus:ring-2 focus:ring-[#f5eef9] placeholder:text-[#a0aec0] transition-all';

onMounted(async () => {
  ownerLookup.value = {
    ...ownerLookup.value,
    imovel_id: route.query.imovel_id || route.query.imovelId || '',
    owner_relationship_id:
      route.query.owner_relationship_id ||
      route.query.ownerRelationshipId ||
      '',
    contact_id: route.query.contact_id || route.query.contactId || '',
    capture_opportunity_id:
      route.query.capture_opportunity_id ||
      route.query.captureOpportunityId ||
      '',
  };

  await Promise.all([
    carregarUltimoImport(),
    carregarXmlFeed(),
    carregarRunsCaptacao(),
    ownerLookup.value.imovel_id
      ? carregarCarteiraProprietario()
      : Promise.resolve(),
  ]);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex flex-col h-full bg-white font-redHatText overflow-hidden"
  >
    <LaisHeader
      title="Captação"
      helper="Importe imóveis dos portais ou do seu site para a prateleira da Lais."
      sticky
    />

    <div class="flex-1 overflow-auto p-4 sm:p-6 lg:p-10">
      <div class="grid w-full gap-5">
        <LaisCard>
          <div
            class="grid items-center gap-5 md:grid-cols-[minmax(0,1fr)_260px]"
          >
            <div>
              <h2
                class="font-redHatDisplay text-[18px] font-semibold text-lais-text"
              >
                Defina as áreas de atuação da imobiliária
              </h2>
              <p class="mt-2 text-[14px] leading-relaxed text-lais-text-body">
                As áreas de atuação são essenciais para que a imobiliária receba
                os melhores leads da Imudei. Essas áreas serão padrão para os
                novos corretores cadastrados e poderão ser editadas
                individualmente a qualquer momento.
              </p>
            </div>
            <div class="flex flex-col gap-2 sm:flex-row md:flex-col">
              <LaisPrimaryButton
                variant="primary"
                icon="i-lucide-map"
                @click="definirAreaAtuacao"
              >
                Definir área de atuação
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-arrow-left"
                @click="voltar"
              >
                Voltar
              </LaisPrimaryButton>
            </div>
          </div>
        </LaisCard>

        <LaisCard title="Proprietário e captação">
          <div class="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_auto]">
            <LaisField label="ID do imóvel" required>
              <input
                v-model.trim="ownerLookup.imovel_id"
                type="text"
                inputmode="numeric"
                :class="input"
                placeholder="123"
                @keyup.enter="carregarCarteiraProprietario"
              />
            </LaisField>
            <LaisField label="Relacionamento">
              <input
                v-model.trim="ownerLookup.owner_relationship_id"
                type="text"
                inputmode="numeric"
                :class="input"
                placeholder="opcional"
                @keyup.enter="carregarCarteiraProprietario"
              />
            </LaisField>
            <LaisField label="Oportunidade">
              <input
                v-model.trim="ownerLookup.capture_opportunity_id"
                type="text"
                inputmode="numeric"
                :class="input"
                placeholder="opcional"
                @keyup.enter="carregarCarteiraProprietario"
              />
            </LaisField>
            <div class="flex items-end">
              <LaisPrimaryButton
                class="w-full"
                variant="primary"
                icon="i-lucide-search"
                :is-loading="isLoadingOwnerPortfolio"
                @click="carregarCarteiraProprietario"
              >
                Carregar
              </LaisPrimaryButton>
            </div>
          </div>

          <div
            v-if="isLoadingOwnerPortfolio"
            class="mt-5 grid gap-3 md:grid-cols-3"
          >
            <div
              v-for="item in 3"
              :key="item"
              class="h-24 animate-pulse rounded-lais-sm bg-[#f3f4f6]"
            />
          </div>

          <div
            v-else-if="ownerPortfolioError"
            class="mt-5 flex items-start gap-3 rounded-lais-sm border border-amber-200 bg-amber-50 p-4"
          >
            <span
              class="i-lucide-lock-keyhole size-5 shrink-0 text-amber-700"
              aria-hidden="true"
            />
            <div class="min-w-0">
              <p class="text-[13px] font-semibold text-amber-800">
                {{ ownerPortfolioError }}
              </p>
              <button
                type="button"
                class="mt-3 inline-flex h-8 items-center gap-2 rounded-lais border border-amber-200 bg-white px-3 text-[12px] font-semibold text-amber-800 transition-colors hover:bg-amber-100"
                @click="carregarCarteiraProprietario"
              >
                <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
                Tentar novamente
              </button>
            </div>
          </div>

          <div v-else-if="ownerPortfolio" class="mt-5 grid gap-5">
            <div class="grid gap-3 md:grid-cols-4">
              <div class="rounded-lais-sm border border-[#e5e7eb] p-3">
                <p class="text-[11px] font-semibold uppercase text-[#a0aec0]">
                  Proprietário
                </p>
                <p
                  class="mt-1 truncate text-[15px] font-semibold text-[#1a1a1a]"
                >
                  {{ ownerDisplayValue(ownerPayload.name) }}
                </p>
                <p class="mt-1 truncate text-[12px] text-[#4a5568]">
                  {{ ownerDisplayValue(ownerPayload.phone_number) }}
                </p>
                <p class="truncate text-[12px] text-[#4a5568]">
                  {{ ownerDisplayValue(ownerPayload.email) }}
                </p>
              </div>
              <div class="rounded-lais-sm border border-[#e5e7eb] p-3">
                <p class="text-[11px] font-semibold uppercase text-[#a0aec0]">
                  Estágio
                </p>
                <p class="mt-1 text-[15px] font-semibold text-[#1a1a1a]">
                  {{ ownerStageLabel(ownerCaptureStage) }}
                </p>
                <p class="mt-1 text-[12px] text-[#4a5568]">
                  Consentimento:
                  {{ ownerCapture.owner_consent_status || '—' }}
                </p>
              </div>
              <div class="rounded-lais-sm border border-[#e5e7eb] p-3">
                <p class="text-[11px] font-semibold uppercase text-[#a0aec0]">
                  Semana
                </p>
                <p class="mt-1 text-[15px] font-semibold text-[#1a1a1a]">
                  {{ ownerFrequency.reveals?.current_user || 0 }}/{{
                    ownerFrequency.reveals?.weekly_limit || 0
                  }}
                  reveals
                </p>
                <p class="mt-1 text-[12px] text-[#4a5568]">
                  {{ ownerFrequency.attempts?.owner_account || 0 }}/{{
                    ownerFrequency.attempts?.risk_threshold || 0
                  }}
                  tentativas
                </p>
              </div>
              <div class="rounded-lais-sm border border-[#e5e7eb] p-3">
                <p class="text-[11px] font-semibold uppercase text-[#a0aec0]">
                  Próxima ação
                </p>
                <p class="mt-1 text-[15px] font-semibold text-[#1a1a1a]">
                  {{ formatShortDateTime(ownerAttempts.next?.at) }}
                </p>
                <p
                  class="mt-1 text-[12px]"
                  :class="
                    ownerAttempts.next?.overdue
                      ? 'font-semibold text-[#b42318]'
                      : 'text-[#4a5568]'
                  "
                >
                  {{ ownerAttempts.next?.overdue ? 'Vencida' : 'No prazo' }}
                </p>
              </div>
            </div>

            <div class="flex flex-wrap gap-2">
              <span
                class="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold"
                :class="
                  ownerCompliance.pii_redacted
                    ? 'border-sky-100 bg-sky-50 text-sky-700'
                    : 'border-emerald-100 bg-emerald-50 text-emerald-700'
                "
              >
                <span
                  :class="
                    ownerCompliance.pii_redacted
                      ? 'i-lucide-eye-off'
                      : 'i-lucide-eye'
                  "
                  class="size-3.5"
                  aria-hidden="true"
                />
                {{
                  ownerCompliance.pii_redacted
                    ? 'PII redigida'
                    : 'Reveal vigente'
                }}
              </span>
              <span
                v-if="ownerCompliance.do_not_contact"
                class="inline-flex items-center gap-1 rounded-full border border-rose-100 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700"
              >
                <span class="i-lucide-ban size-3.5" aria-hidden="true" />
                DNC
              </span>
              <span
                v-if="ownerCompliance.blacklisted"
                class="inline-flex items-center gap-1 rounded-full border border-rose-100 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700"
              >
                <span
                  class="i-lucide-shield-alert size-3.5"
                  aria-hidden="true"
                />
                Blacklist
              </span>
              <span
                v-for="flag in ownerRiskFlags"
                :key="flag.code"
                class="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold"
                :class="ownerRiskClass(flag.severity)"
              >
                <span class="i-lucide-activity size-3.5" aria-hidden="true" />
                {{ ownerRiskLabel(flag.code) }}
              </span>
            </div>

            <div class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
              <div class="rounded-lais-sm border border-[#e5e7eb]">
                <div
                  class="flex items-center justify-between gap-3 border-b border-[#f3f4f6] px-4 py-3"
                >
                  <h3 class="text-[14px] font-semibold text-[#1a1a1a]">
                    Carteira
                  </h3>
                  <span class="text-[12px] text-[#6b7280]">
                    {{ ownerPortfolioProperties.length }} imóveis
                  </span>
                </div>
                <ul class="divide-y divide-[#f3f4f6]">
                  <li
                    v-for="property in ownerPortfolioProperties"
                    :key="`${property.imovel_id}-${property.relationship_id}`"
                    class="grid gap-2 px-4 py-3 md:grid-cols-[minmax(0,1fr)_auto]"
                  >
                    <div class="min-w-0">
                      <p
                        class="truncate text-[13px] font-semibold text-[#1a1a1a]"
                      >
                        {{ property.title || `Imóvel #${property.imovel_id}` }}
                      </p>
                      <p class="mt-1 text-[12px] text-[#6b7280]">
                        {{ property.bairro || '—' }} ·
                        {{ property.cidade || '—' }} ·
                        {{ property.finalidade || '—' }}
                      </p>
                    </div>
                    <div
                      class="flex flex-wrap items-center gap-2 md:justify-end"
                    >
                      <span
                        class="rounded-full bg-[#f3f4f6] px-2 py-0.5 text-[11px] font-semibold text-[#4a5568]"
                      >
                        {{ ownerStageLabel(property.capture_stage) }}
                      </span>
                      <span
                        v-if="property.do_not_contact"
                        class="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700"
                      >
                        DNC
                      </span>
                    </div>
                  </li>
                </ul>
              </div>

              <div class="rounded-lais-sm border border-[#e5e7eb] p-4">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h3 class="text-[14px] font-semibold text-[#1a1a1a]">
                      Registrar tentativa
                    </h3>
                    <p class="mt-1 text-[12px] text-[#6b7280]">
                      Última:
                      {{ ownerAttempts.last?.result || '—' }} ·
                      {{ formatShortDateTime(ownerAttempts.last?.occurred_at) }}
                    </p>
                  </div>
                  <span
                    v-if="ownerAttemptBlockedReason"
                    class="rounded-full bg-[#f3f4f6] px-2 py-0.5 text-[11px] font-semibold text-[#4a5568]"
                  >
                    {{ ownerAttemptBlockedReason }}
                  </span>
                </div>

                <div class="mt-4 grid gap-3">
                  <LaisField label="Canal">
                    <select v-model="ownerAttemptForm.channel" :class="input">
                      <option
                        v-for="channel in OWNER_CAPTURE_CHANNELS"
                        :key="channel.value"
                        :value="channel.value"
                      >
                        {{ channel.label }}
                      </option>
                    </select>
                  </LaisField>
                  <LaisField label="Resultado">
                    <select v-model="ownerAttemptForm.result" :class="input">
                      <option
                        v-for="result in OWNER_CAPTURE_RESULTS"
                        :key="result.value"
                        :value="result.value"
                      >
                        {{ result.label }}
                      </option>
                    </select>
                  </LaisField>
                  <LaisField
                    label="Próxima ação"
                    :required="ownerAttemptRequiresNextAction"
                  >
                    <input
                      v-model="ownerAttemptForm.next_action_at"
                      type="datetime-local"
                      :disabled="!ownerAttemptRequiresNextAction"
                      :class="input"
                    />
                  </LaisField>
                  <LaisField label="Responsável">
                    <input
                      v-model.trim="ownerAttemptForm.responsible_user_id"
                      type="text"
                      inputmode="numeric"
                      :class="input"
                      placeholder="ID do usuário"
                    />
                  </LaisField>
                  <LaisField label="Motivo">
                    <input
                      v-model.trim="ownerAttemptForm.reason"
                      type="text"
                      maxlength="120"
                      :class="input"
                      placeholder="ex.: retorno combinado"
                    />
                  </LaisField>
                  <LaisPrimaryButton
                    variant="primary"
                    icon="i-lucide-check"
                    :disabled="!ownerCanRecordAttempt"
                    :is-loading="isRecordingOwnerAttempt"
                    @click="registrarTentativaProprietario"
                  >
                    Registrar
                  </LaisPrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </LaisCard>

        <LaisCard v-if="importStatusError">
          <div class="flex gap-4">
            <div
              class="grid size-11 shrink-0 place-items-center rounded-lais bg-[#fff7ed] text-[#c2410c]"
            >
              <span class="i-lucide-circle-alert size-5" aria-hidden="true" />
            </div>
            <div>
              <h2
                class="font-redHatDisplay text-[16px] font-semibold text-lais-text"
              >
                {{ t('CAPTACAO.ERROR_STATE.TITLE') }}
              </h2>
              <p class="mt-1 text-[13px] leading-relaxed text-lais-text-body">
                {{ t('CAPTACAO.ERROR_STATE.DESCRIPTION') }}
              </p>
              <LaisPrimaryButton
                class="mt-4"
                variant="ghost"
                icon="i-lucide-refresh-cw"
                @click="carregarUltimoImport"
              >
                {{ t('CAPTACAO.ERROR_STATE.RETRY') }}
              </LaisPrimaryButton>
            </div>
          </div>
        </LaisCard>

        <LaisCard title="Prateleira de anúncios">
          <CaptacaoShelfSkeleton v-if="isLoadingXmlFeed" />

          <div
            v-else
            class="mb-5 grid gap-3 rounded-lais-sm border border-[#eee5d6] bg-[#fafafa] p-4 lg:grid-cols-[minmax(0,1fr)_auto]"
          >
            <div>
              <div class="flex flex-wrap items-center gap-2">
                <span
                  class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide"
                  :class="xmlFeedStatusMeta(xmlFeed?.status).cls"
                >
                  {{ xmlFeedStatusMeta(xmlFeed?.status).label }}
                </span>
                <span class="text-[12px] text-[#6b7280]">
                  {{ xmlFeed?.listings_imported || 0 }} imóveis úteis ·
                  {{ xmlFeed?.listings_found || 0 }} encontrados no XML
                </span>
              </div>
              <p class="mt-2 text-[13px] text-[#4a5568]">
                Última validação:
                <strong class="text-[#1a1a1a]">
                  {{ formatDateTime(xmlFeed?.last_validated_at) }}
                </strong>
                · Último sucesso:
                <strong class="text-[#1a1a1a]">
                  {{ formatDateTime(xmlFeed?.last_success_at) }}
                </strong>
              </p>
              <p
                v-if="xmlFeed?.last_error"
                class="mt-2 text-[13px] font-semibold text-[#b42318]"
              >
                {{ xmlFeed.last_error }}
              </p>
            </div>
            <div v-if="xmlFeed?.status !== 'disabled'" class="grid gap-2">
              <input
                v-model.trim="disableReason"
                type="text"
                :class="input"
                maxlength="140"
                placeholder="Motivo para desativar"
              />
              <button
                type="button"
                class="inline-flex h-9 items-center justify-center gap-2 rounded-lais border border-[#ffd6d6] px-3 text-[12px] font-semibold text-[#b42318] transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="isDisablingXml"
                @click="desativarXmlFeed"
              >
                <span class="i-lucide-power-off size-4" aria-hidden="true" />
                Desativar
              </button>
            </div>
          </div>

          <div
            v-if="xmlFeed?.warnings?.length"
            class="mb-5 rounded-lais-sm border border-amber-200 bg-amber-50 p-3"
          >
            <p class="text-[12px] font-semibold uppercase text-amber-700">
              Pendências encontradas
            </p>
            <ul class="mt-2 grid gap-1 text-[13px] text-amber-800">
              <li v-for="warning in xmlFeed.warnings" :key="warning">
                {{ warning }}
              </li>
            </ul>
          </div>

          <div
            class="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4"
          >
            <label
              v-for="p in PORTAIS"
              :key="p.value"
              class="flex flex-col items-center gap-2 p-3 border rounded-lais cursor-pointer transition-colors text-center"
              :class="
                form.portal === p.value
                  ? 'border-[#49317a] bg-[#f0e8fa]/40'
                  : 'border-[#e5e7eb] hover:border-[#49317a]/40'
              "
            >
              <input
                v-model="form.portal"
                type="radio"
                :value="p.value"
                class="hidden"
              />
              <span
                class="size-5"
                :class="p.icon"
                :style="{ color: p.color }"
              />
              <span
                class="text-[12px] font-medium text-[#1a1a1a] leading-tight"
              >
                {{ p.label }}
              </span>
            </label>
          </div>

          <LaisField label="URL do XML" required>
            <input
              v-model="form.url"
              type="url"
              :class="input"
              placeholder="https://www.exemplo.com.br/feed.xml"
            />
            <div
              class="mt-2 rounded-lais-sm border border-[#e5e7eb] bg-[#faf8fd] px-3 py-2"
            >
              <button
                type="button"
                class="flex w-full items-center justify-between gap-3 text-left text-[12px] font-semibold text-[#49317a]"
                :aria-expanded="showXmlHelp"
                @click="showXmlHelp = !showXmlHelp"
              >
                <span class="inline-flex items-center gap-2">
                  <span
                    class="i-lucide-circle-help size-4"
                    aria-hidden="true"
                  />
                  Não encontro meu XML
                </span>
                <span
                  class="i-lucide-chevron-down size-4 transition-transform"
                  :class="showXmlHelp ? 'rotate-180' : ''"
                  aria-hidden="true"
                />
              </button>
              <div
                v-if="showXmlHelp"
                class="mt-3 grid gap-3 border-t border-[#e5e7eb] pt-3 text-[12px] leading-relaxed text-[#4a5568]"
              >
                <p>
                  Procure no {{ selectedPortal.label }} por menus como
                  Integrações, Exportação, Feed XML, VRSync ou Anúncios.
                </p>
                <ol class="grid gap-1 pl-4 list-decimal">
                  <li>
                    Copie a URL pública do feed de imóveis, não a URL do painel
                    administrativo.
                  </li>
                  <li>
                    Abra o link em uma aba anônima: ele deve carregar sem pedir
                    login.
                  </li>
                  <li>
                    Se o link não existir, peça ao suporte do portal ou CRM para
                    liberar o feed XML/VRSync da sua conta.
                  </li>
                </ol>
                <a
                  :href="xmlSupportMailto"
                  class="inline-flex w-fit items-center gap-2 rounded-lais border border-[#49317a]/20 bg-white px-3 py-2 text-[12px] font-semibold text-[#49317a] transition-colors hover:bg-[#f0e8fa]"
                >
                  <span class="i-lucide-mail size-4" aria-hidden="true" />
                  Pedir liberação ao suporte
                </a>
              </div>
            </div>
          </LaisField>

          <LaisField
            label="SLA de atualização"
            helper="Horas até a prateleira ficar desatualizada"
            class="mt-4 max-w-xs"
          >
            <input
              v-model.number="form.refresh_interval_hours"
              type="number"
              min="1"
              max="720"
              :class="input"
            />
          </LaisField>

          <div class="mt-5 flex flex-wrap items-center gap-3">
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-shield-check"
              :is-loading="isValidatingXml"
              @click="validarXml"
            >
              Validar XML
            </LaisPrimaryButton>
            <LaisPrimaryButton
              variant="primary"
              icon="i-lucide-download"
              :is-loading="isImporting"
              @click="importar"
            >
              {{ isImporting ? 'Importando…' : 'Importar agora' }}
            </LaisPrimaryButton>
            <p class="text-[12px] text-[#a0aec0]">
              {{
                xmlFeedHelp.body || 'A URL deve abrir no navegador sem login.'
              }}
            </p>
          </div>
        </LaisCard>

        <LaisCard v-if="lastImport" title="Última importação">
          <div class="grid gap-2 text-[13px]">
            <p>
              <span class="text-[#a0aec0]">Portal:</span>
              <strong class="text-[#1a1a1a] ml-2">{{
                lastImport.portal || '—'
              }}</strong>
            </p>
            <p>
              <span class="text-[#a0aec0]">URL:</span>
              <code
                class="ml-2 px-1.5 py-0.5 text-[11px] bg-[#fafafa] rounded text-[#49317a] font-mono"
              >
                {{ lastImport.url }}
              </code>
            </p>
            <p>
              <span class="text-[#a0aec0]">Enfileirado em:</span>
              <strong class="text-[#1a1a1a] ml-2 tabular-nums">
                {{
                  lastImport.enqueued_at &&
                  new Date(lastImport.enqueued_at).toLocaleString('pt-BR')
                }}
              </strong>
            </p>
            <p v-if="lastImport.completed_at">
              <span class="text-[#a0aec0]">Concluído em:</span>
              <strong class="text-[#1a1a1a] ml-2 tabular-nums">
                {{ new Date(lastImport.completed_at).toLocaleString('pt-BR') }}
              </strong>
            </p>
            <p v-if="lastImport.created != null || lastImport.updated != null">
              <span class="text-[#a0aec0]">Resultado:</span>
              <strong class="text-[#1a1a1a] ml-2">
                {{ lastImport.created || 0 }} criados ·
                {{ lastImport.updated || 0 }} atualizados ·
                {{ lastImport.failed || 0 }} falhas
              </strong>
            </p>
            <div v-if="lastImport.status === 'enqueued'" class="pt-2">
              <button
                type="button"
                class="inline-flex items-center gap-2 h-9 px-3 text-[13px] font-semibold text-[#FF8585] hover:text-white hover:bg-[#FF8585] border border-[#FF8585] rounded-lais transition-colors"
                @click="solicitarCancelamento"
              >
                <span class="i-lucide-x-circle size-4" aria-hidden="true" />
                Cancelar importação
              </button>
            </div>
          </div>
        </LaisCard>

        <LaisCard title="Runs, proveniência e integridade">
          <div class="grid gap-3 lg:grid-cols-[160px_minmax(0,1fr)_auto]">
            <LaisField label="Status">
              <select v-model="runFilters.status" :class="input">
                <option
                  v-for="option in runStatusOptions"
                  :key="option.value || 'all'"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </LaisField>
            <LaisField label="Origem">
              <input
                v-model.trim="runFilters.origin"
                type="search"
                :class="input"
                placeholder="portal, XML ou chave da fonte"
                @keyup.enter="aplicarFiltrosRuns"
              />
            </LaisField>
            <div class="flex items-end gap-2">
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-filter"
                @click="aplicarFiltrosRuns"
              >
                Filtrar
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-refresh-cw"
                :is-loading="isLoadingImportRuns"
                @click="carregarRunsCaptacao"
              >
                Atualizar
              </LaisPrimaryButton>
            </div>
          </div>

          <div class="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div class="rounded-lais-sm border border-[#e5e7eb] p-3">
              <p class="text-[11px] font-semibold uppercase text-[#a0aec0]">
                Runs
              </p>
              <p class="mt-1 text-[18px] font-semibold text-[#1a1a1a]">
                {{ importRunSummary.total }}
              </p>
            </div>
            <div class="rounded-lais-sm border border-[#e5e7eb] p-3">
              <p class="text-[11px] font-semibold uppercase text-[#a0aec0]">
                Ativos
              </p>
              <p class="mt-1 text-[18px] font-semibold text-sky-700">
                {{ importRunSummary.active }}
              </p>
            </div>
            <div class="rounded-lais-sm border border-[#e5e7eb] p-3">
              <p class="text-[11px] font-semibold uppercase text-[#a0aec0]">
                Falhas
              </p>
              <p class="mt-1 text-[18px] font-semibold text-rose-700">
                {{ importRunSummary.failed }}
              </p>
            </div>
            <div class="rounded-lais-sm border border-[#e5e7eb] p-3">
              <p class="text-[11px] font-semibold uppercase text-[#a0aec0]">
                Retry
              </p>
              <p class="mt-1 text-[18px] font-semibold text-amber-700">
                {{ importRunSummary.retryable }}
              </p>
            </div>
            <div class="rounded-lais-sm border border-[#e5e7eb] p-3">
              <p class="text-[11px] font-semibold uppercase text-[#a0aec0]">
                Quarentena
              </p>
              <p class="mt-1 text-[18px] font-semibold text-[#49317a]">
                {{ importRunSummary.quarantined }}
              </p>
            </div>
          </div>

          <p v-if="runAuditError" class="mt-4 text-[13px] text-rose-700">
            {{ runAuditError }}
          </p>
          <p
            v-else-if="isLoadingImportRuns"
            class="mt-6 text-center text-[13px] text-[#6b7280]"
          >
            Carregando runs de importação...
          </p>
          <p
            v-else-if="!hasImportRuns"
            class="mt-6 text-center text-[13px] text-[#6b7280]"
          >
            Nenhum run encontrado para os filtros atuais.
          </p>

          <div v-else class="mt-5 grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
            <div class="min-w-0 overflow-auto">
              <table class="w-full min-w-[720px] text-left text-[13px]">
                <thead>
                  <tr class="border-b border-[#e5e7eb] text-[#6b7280]">
                    <th class="py-2 pr-3 font-semibold">Fonte/run</th>
                    <th class="py-2 pr-3 font-semibold">Status</th>
                    <th class="py-2 pr-3 font-semibold">Itens</th>
                    <th class="py-2 pr-3 font-semibold">Timestamp</th>
                    <th class="py-2 text-right font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="run in importRuns"
                    :key="run.id"
                    class="border-b border-[#f3f4f6] align-top"
                  >
                    <td class="py-3 pr-3">
                      <p class="font-semibold text-[#1a1a1a]">
                        {{ sourceLabelForRun(run) }}
                      </p>
                      <p class="mt-1 text-[12px] text-[#6b7280]">
                        Run #{{ run.id }} ·
                        {{ run.importMode || 'incremental' }}
                      </p>
                      <p
                        class="mt-1 max-w-[260px] truncate text-[11px] text-[#a0aec0]"
                      >
                        {{ run.sourceUrl || run.token }}
                      </p>
                    </td>
                    <td class="py-3 pr-3">
                      <span
                        class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase"
                        :class="runToneClass(run.status)"
                      >
                        {{ runStatusLabel(run.status) }}
                      </span>
                      <p
                        v-if="run.active"
                        class="mt-1 text-[11px] text-sky-700"
                      >
                        Em execução
                      </p>
                    </td>
                    <td class="py-3 pr-3">
                      <div class="flex flex-wrap gap-1">
                        <span
                          v-for="entry in rawStatusEntries(run)"
                          :key="`${run.id}-${entry.status}`"
                          class="rounded-lais-sm bg-[#f3f4f6] px-2 py-0.5 text-[11px] text-[#4a5568]"
                        >
                          {{ entry.status }}: {{ entry.count }}
                        </span>
                      </div>
                      <p
                        v-if="run.retryableRawRecordsCount"
                        class="mt-1 text-[11px] font-semibold text-rose-700"
                      >
                        {{ run.retryableRawRecordsCount }} retry/replay
                      </p>
                    </td>
                    <td class="py-3 pr-3 text-[12px] text-[#4a5568]">
                      {{
                        formatShortDateTime(
                          run.completedAt || run.updatedAt || run.createdAt
                        )
                      }}
                    </td>
                    <td class="py-3 text-right">
                      <div class="flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          class="rounded-lais border border-[#e5e7eb] px-2 py-1 text-[12px] font-semibold text-[#49317a] hover:bg-[#f0e8fa]"
                          @click="carregarDetalheRun(run)"
                        >
                          Detalhes
                        </button>
                        <button
                          type="button"
                          class="rounded-lais border border-[#e5e7eb] px-2 py-1 text-[12px] font-semibold text-[#4a5568] hover:bg-[#f3f4f6] disabled:cursor-not-allowed disabled:opacity-50"
                          :disabled="
                            !canRetryRun(run) ||
                            isRunActionLoading(run, 'retry')
                          "
                          @click="retryRunCaptacao(run)"
                        >
                          Retry
                        </button>
                        <button
                          type="button"
                          class="rounded-lais border border-[#e5e7eb] px-2 py-1 text-[12px] font-semibold text-[#4a5568] hover:bg-[#f3f4f6] disabled:cursor-not-allowed disabled:opacity-50"
                          :disabled="
                            !canReconcileRun(run) ||
                            isRunActionLoading(run, 'reconcile')
                          "
                          @click="reconciliarRunCaptacao(run)"
                        >
                          Reconciliar
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div
                class="mt-4 flex items-center justify-between text-[12px] text-[#6b7280]"
              >
                <button
                  type="button"
                  class="rounded-lais border border-[#e5e7eb] px-3 py-1.5 font-semibold disabled:opacity-50"
                  :disabled="!hasPreviousRunPage"
                  @click="mudarPaginaRuns(-1)"
                >
                  Anterior
                </button>
                <span>
                  Página {{ importRunsMeta.page || 1 }} de
                  {{ importRunsMeta.total_pages || 0 }}
                </span>
                <button
                  type="button"
                  class="rounded-lais border border-[#e5e7eb] px-3 py-1.5 font-semibold disabled:opacity-50"
                  :disabled="!hasNextRunPage"
                  @click="mudarPaginaRuns(1)"
                >
                  Próxima
                </button>
              </div>
            </div>

            <div class="grid gap-4">
              <div class="rounded-lais-sm border border-[#e5e7eb] p-4">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <p class="text-[14px] font-semibold text-[#1a1a1a]">
                      Run selecionado
                    </p>
                    <p class="mt-1 text-[12px] text-[#6b7280]">
                      {{
                        selectedAuditRun
                          ? `#${selectedAuditRun.id} · ${sourceLabelForRun(selectedAuditRun)}`
                          : 'Selecione um run para auditar'
                      }}
                    </p>
                  </div>
                  <span
                    v-if="isLoadingRunDetail"
                    class="i-lucide-loader-circle size-4 animate-spin text-[#6b7280]"
                    aria-hidden="true"
                  />
                </div>

                <div v-if="selectedAuditRun" class="mt-4 grid gap-3">
                  <div
                    v-if="
                      selectedAuditRun.status === 'failed' ||
                      selectedAuditRun.errorMessage
                    "
                    class="rounded-lais-sm border border-rose-100 bg-rose-50 p-3"
                  >
                    <p
                      class="text-[12px] font-semibold uppercase text-rose-700"
                    >
                      Detalhe da falha
                    </p>
                    <p class="mt-1 text-[13px] text-rose-800">
                      {{
                        selectedAuditRun.errorMessage ||
                        'Falha registrada sem mensagem detalhada.'
                      }}
                    </p>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <LaisPrimaryButton
                        variant="ghost"
                        icon="i-lucide-refresh-cw"
                        size="sm"
                        :disabled="!canRetrySelectedRun"
                        :is-loading="
                          isRunActionLoading(selectedAuditRun, 'retry')
                        "
                        @click="retryRunCaptacao(selectedAuditRun)"
                      >
                        Retry permitido
                      </LaisPrimaryButton>
                      <LaisPrimaryButton
                        variant="ghost"
                        icon="i-lucide-shuffle"
                        size="sm"
                        :disabled="!canReconcileSelectedRun"
                        :is-loading="
                          isRunActionLoading(selectedAuditRun, 'reconcile')
                        "
                        @click="reconciliarRunCaptacao(selectedAuditRun)"
                      >
                        Reconciliação
                      </LaisPrimaryButton>
                    </div>
                  </div>

                  <LaisField label="Motivo da ação operacional">
                    <textarea
                      v-model.trim="runOperationReason"
                      rows="3"
                      class="min-h-[88px]"
                      :class="input"
                      placeholder="ex.: corrigir itens em quarentena apos revisão"
                    />
                  </LaisField>

                  <div class="rounded-lais-sm border border-[#e5e7eb] p-3">
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <p class="text-[13px] font-semibold text-[#1a1a1a]">
                          Integridade por run
                        </p>
                        <p class="mt-1 text-[12px] text-[#6b7280]">
                          Saúde: {{ selectedRunIntegrity.health }} ·
                          {{ selectedRunIntegrity.errorCount }} erros ·
                          {{ selectedRunIntegrity.retryableCount }} retry
                        </p>
                      </div>
                      <span
                        class="rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase"
                        :class="
                          hasSelectedRunErrors
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-emerald-50 text-emerald-700'
                        "
                      >
                        {{ hasSelectedRunErrors ? 'Atenção' : 'Íntegro' }}
                      </span>
                    </div>
                    <ul
                      v-if="hasSelectedRunErrors"
                      class="mt-3 grid gap-2 text-[12px] text-[#4a5568]"
                    >
                      <li
                        v-for="error in selectedRunIntegrity.errors"
                        :key="`${error.code}-${error.itemId}-${error.message}`"
                        class="rounded-lais-sm bg-[#fafafa] p-2"
                      >
                        <p class="font-semibold text-[#1a1a1a]">
                          {{ error.code }}
                        </p>
                        <p class="mt-1">{{ error.message }}</p>
                        <p v-if="error.suggestion" class="mt-1 text-[#6b7280]">
                          Sugestão: {{ error.suggestion }}
                        </p>
                      </li>
                    </ul>
                    <p v-else class="mt-3 text-[12px] text-[#6b7280]">
                      Nenhum erro de integridade retornado para este run.
                    </p>
                  </div>

                  <div class="rounded-lais-sm border border-[#e5e7eb] p-3">
                    <div class="flex items-center justify-between gap-3">
                      <p class="text-[13px] font-semibold text-[#1a1a1a]">
                        Itens impactados e proveniência
                      </p>
                      <RouterLink
                        :to="importOperationsRoute"
                        class="text-[12px] font-semibold text-[#49317a] hover:underline"
                      >
                        Console completo
                      </RouterLink>
                    </div>
                    <p
                      v-if="!selectedRunProvenanceItems.length"
                      class="mt-3 text-[12px] text-[#6b7280]"
                    >
                      Abra os detalhes do run para listar itens impactados.
                    </p>
                    <div v-else class="mt-3 grid gap-3">
                      <article
                        v-for="item in selectedRunProvenanceItems"
                        :key="item.id"
                        class="rounded-lais-sm border border-[#f3f4f6] p-3"
                      >
                        <div class="flex items-start justify-between gap-3">
                          <div class="min-w-0">
                            <p
                              class="truncate text-[13px] font-semibold text-[#1a1a1a]"
                            >
                              {{ item.externalId }}
                            </p>
                            <p class="mt-1 text-[12px] text-[#6b7280]">
                              Fonte: {{ item.source }} · Run #{{ item.runId }}
                            </p>
                            <p class="mt-1 text-[12px] text-[#6b7280]">
                              Timestamp:
                              {{ formatShortDateTime(item.timestamp) }} ·
                              Agente:
                              {{ item.agent }}
                            </p>
                          </div>
                          <span
                            class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase"
                            :class="runToneClass(item.status)"
                          >
                            {{ item.status }}
                          </span>
                        </div>

                        <div class="mt-3 rounded-lais-sm bg-[#fafafa] p-2">
                          <p
                            class="text-[11px] font-semibold uppercase text-[#6b7280]"
                          >
                            Payload de origem redigido
                          </p>
                          <dl
                            class="mt-2 grid gap-1 text-[11px] text-[#4a5568]"
                          >
                            <div
                              v-for="entry in item.payloadEntries"
                              :key="`${item.id}-${entry.key}`"
                              class="grid gap-1 sm:grid-cols-[120px_minmax(0,1fr)]"
                            >
                              <dt class="font-semibold text-[#6b7280]">
                                {{ entry.key }}
                              </dt>
                              <dd class="min-w-0 break-all font-mono">
                                {{ entry.value }}
                              </dd>
                            </div>
                          </dl>
                        </div>

                        <div class="mt-3 flex flex-wrap gap-2">
                          <RouterLink
                            v-if="item.imovelId"
                            :to="imovelRoute(item.imovelId)"
                            class="inline-flex items-center gap-1 rounded-lais border border-[#e5e7eb] px-2 py-1 text-[12px] font-semibold text-[#49317a] hover:bg-[#f0e8fa]"
                          >
                            <span
                              class="i-lucide-home size-3.5"
                              aria-hidden="true"
                            />
                            Imóvel #{{ item.imovelId }}
                          </RouterLink>
                          <span
                            v-for="listingId in item.listingIds"
                            :key="`${item.id}-listing-${listingId}`"
                            class="inline-flex items-center gap-1 rounded-lais border border-[#e5e7eb] px-2 py-1 text-[12px] text-[#4a5568]"
                          >
                            Anúncio #{{ listingId }}
                          </span>
                        </div>
                      </article>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LaisCard>

        <LaisCard
          v-if="importHistory.length > 1"
          title="Histórico recente de importações"
        >
          <ul class="flex flex-col gap-3">
            <li
              v-for="item in importHistory"
              :key="item.token || item.enqueued_at"
              class="flex items-start justify-between gap-4 border border-[#f3f4f6] rounded-lais p-3"
            >
              <div class="min-w-0">
                <p class="text-[13px] font-semibold text-[#1a1a1a]">
                  {{ item.portal || 'XML' }}
                </p>
                <p class="text-[12px] text-[#a0aec0] truncate">
                  {{ item.url }}
                </p>
                <p class="text-[12px] text-[#a0aec0] mt-1">
                  {{
                    item.enqueued_at &&
                    new Date(item.enqueued_at).toLocaleString('pt-BR')
                  }}
                </p>
              </div>
              <span
                class="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
                :class="importStatusMeta(item.status).cls"
              >
                {{ importStatusMeta(item.status).label }}
              </span>
            </li>
          </ul>
        </LaisCard>

        <LaisCard title="Como funciona">
          <ol class="flex flex-col gap-3 text-[13px] text-[#4a5568]">
            <li class="flex gap-3">
              <span
                class="size-6 shrink-0 rounded-full bg-[#f0e8fa] text-[#49317a] grid place-items-center text-[12px] font-semibold"
              >
                1
              </span>
              <span>
                <strong class="text-[#1a1a1a]">Selecione o portal</strong> de
                origem dos imóveis.
              </span>
            </li>
            <li class="flex gap-3">
              <span
                class="size-6 shrink-0 rounded-full bg-[#f0e8fa] text-[#49317a] grid place-items-center text-[12px] font-semibold"
              >
                2
              </span>
              <span>
                <strong class="text-[#1a1a1a]">Cole a URL do XML</strong>
                (formato VRSync, ZAP, OLX ou Bubble.io ImobiBrasil).
              </span>
            </li>
            <li class="flex gap-3">
              <span
                class="size-6 shrink-0 rounded-full bg-[#f0e8fa] text-[#49317a] grid place-items-center text-[12px] font-semibold"
              >
                3
              </span>
              <span>
                A Lais identifica cada imóvel pelo
                <strong class="text-[#1a1a1a]">código de anúncio</strong>
                e atualiza dados existentes ou cria novos.
              </span>
            </li>
            <li class="flex gap-3">
              <span
                class="size-6 shrink-0 rounded-full bg-[#f0e8fa] text-[#49317a] grid place-items-center text-[12px] font-semibold"
              >
                4
              </span>
              <span>
                Os imóveis aparecem em
                <strong class="text-[#1a1a1a]">Empreendimentos</strong>
                e ficam disponíveis para conversas e visitas.
              </span>
            </li>
          </ol>
        </LaisCard>
      </div>
    </div>

    <LaisConfirmModal
      :show="showCancelModal"
      title="Cancelar importação"
      description="Deseja cancelar a importação XML em andamento? Os dados já processados serão mantidos."
      confirm-text="Cancelar importação"
      :is-loading="isCancelling"
      @close="showCancelModal = false"
      @confirm="cancelarImport"
    >
      <div class="mt-4 grid gap-3">
        <LaisField label="Motivo do cancelamento" required>
          <textarea
            v-model.trim="cancelReason"
            rows="3"
            class="min-h-[88px]"
            :class="input"
            maxlength="240"
            placeholder="ex.: URL incorreta ou importação duplicada"
          />
        </LaisField>
        <p class="text-[12px] text-[#6b7280]">
          Trilha: token {{ lastImport?.token || 'sem token' }} · run em
          {{ formatShortDateTime(lastImport?.enqueued_at) }}
        </p>
      </div>
    </LaisConfirmModal>
  </section>
</template>
